/* eslint-disable no-useless-escape */
import * as yup from 'yup';

/**
 * Validation formula for texts (accepts any letters and numbers)
 * @param {string} message - error message to return
 */
export const validateText = (message: string = 'Invalid text format') =>
  yup.string().matches(/^[-'a-zA-ZÀ-ÖØ-öø-ÿ$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^ 0-9]+$/, message);

/**
 * Validation formula for number (accepts only numbers)
 * @param {string} message - error message to return
 */
export const validateNumber = (message: string = 'Invalid number format') => yup.string().matches(/^[0-9]+$/, message);

/**
 * Validation formula for number (accepts only numbers)
 * @param {string} message - error message to return
 */
export const validateNumberArray = (message: string = 'Invalid number[] format') =>
  yup.string().matches(/\[[0-9,]+[0-9]*\]/, message);

/**
 * Validation formula for number (accepts only numbers)
 * @param {string} message - error message to return
 */
export const validateObject = (message: string = 'Invalid JSON') => yup.object().typeError(message);

/**
 * Validation formula for names (accepts any letters)
 * @param {string} message - error message to return
 */
export const validateName = (message: string = 'Invalid name format') =>
  yup
    .string()
    .matches(/^[-' a-zA-ZÀ-ÖØ-öø-ÿ0-9]+$/, message)
    .max(40, 'Character limit error - must enter 1-40 characters');

/**
 * Validation formula for emails
 * @param {string} message - error message to return
 */
export const validateEmail = (message: string = 'Invalid email format') => yup.string().email(message);

/**
 * Validation formula for pins (accepts any numbers that is 5 digits long)
 * @param {string} message - error message to return
 */
export const validatePin = (message: string = 'Invalid pin format') => yup.string().matches(/^[0-9]{5}$/, message);

/**
 * Validation formula for times
 * @param {string} message - error message to return
 */
export const validateTime = (message: string = 'Invalid time') =>
  yup.string().matches(/^(10|11|12|[1-9]):[0-5][0-9]\s*(am|pm|AM|PM)$/, message);

/**
 * Validation formula for file names (accepts any word,underscore and dash)
 * @param {string} message - error message to return
 */
// eslint-disable react-hooks/exhaustive-deps
export const validateFileName = (message: string = 'Invalid file name format') =>
  yup.string().matches(/^[\w\-]+$/, message);

/**
 * Validation formula for passwords
 * @param {string} value - input text to check
 */
export const checkPassword = (value: string) => {
  const PWD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^])[A-Za-z\d$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^]{8,}/;
  const ALPHA_REGEX = /^(?=.*[a-z])(?=.*[A-Z])/;
  const NUMBER_REGEX = /^(?=.*\d)/;
  const SPL_CHAR_REGEX = /^(?=.*[$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^])/;
  const LENGTH_REGEX = /^[A-Za-z\d$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^]{8,}/;
  const validationState = { isInValid: true, isAlpha: false, isNumber: false, isSplChar: false, isLength: false };
  if (PWD_REGEX.test(value)) {
    validationState.isInValid = false;
    validationState.isAlpha = true;
    validationState.isNumber = true;
    validationState.isSplChar = true;
    validationState.isLength = true;
  } else {
    validationState.isInValid = true;
    validationState.isAlpha = ALPHA_REGEX.test(value);
    validationState.isNumber = NUMBER_REGEX.test(value);
    validationState.isSplChar = SPL_CHAR_REGEX.test(value);
    validationState.isLength = value ? LENGTH_REGEX.test(value) : false;
  }

  return validationState;
};

/**
 * Validation formula for passwords
 * @param {string} message - error message to return
 */
export const validatePassword = (message: string = 'Invalid password') => {
  const PWD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^])[A-Za-z\d$@$!%*?&#_<>{}+= :;"\\`~.,\/\)\(\]\[|^]{8,}/;
  return yup.string().matches(PWD_REGEX, message);
};

/**
 * Validation formula for images
 * @param {File} file - uploaded file
 */
export const isValidImage = (file: File, imageType?: string) => {
  let errMsg = '';
  if (file.size > 10485760) {
    errMsg = 'File size is larger than 10MB';
  } else {
    const type = file.type.split('/')[0];
    const subType = file.type.split('/').pop();
    if (type === 'image') {
      if (imageType === 'logo-image' || imageType === 'logo-image-large') {
        // jpeg images cannot be used for sms, so require png for both logos
        if (subType !== 'png') {
          errMsg = 'File type not supported';
        }
      } else {
        if (subType !== 'png' && subType !== 'jpeg') {
          errMsg = 'File type not supported';
        }
      }
    } else {
      errMsg = 'File type not supported';
    }
  }

  return errMsg;
};

/**
 * Validation formula for video
 * @param {File} file - uploaded file
 */
export const isValidVideo = (file: File) => {
  let errMsg = '';
  if (file.size > 104857600) {
    errMsg = 'File size is larger than 100MB';
  } else {
    const type = file.type.split('/')[0];
    const subType = file.type.split('/').pop();
    if (type !== 'video') {
      errMsg = 'File type not supported';
    } else {
      if (subType !== 'mp4' && subType !== 'quicktime') {
        errMsg = 'File type not supported';
      }
    }
  }

  return errMsg;
};

/**
 * Validation formula for image resolution
 * @param {string} imageType - logo image type
 * @param {number} height - image height
 * @param {number} width - image width
 */
export const isValidResolution = (imageType: string, height: number, width: number) => {
  let errMsg = '';
  if (imageType === 'logo-image-large' && (height !== 100 || width !== 320)) {
    errMsg = 'Image dimensions must be 320px by 100px';
  }
  if (imageType === 'logo-image' && (height !== 50 || width !== 160)) {
    errMsg = 'Image dimensions must be 160px by 50px';
  }
  return errMsg;
};
